import { createHash } from 'crypto-browserify';
import { CALL_PRIVATE_API } from 'js/constants/api';
import { POST } from 'js/constants/requestMethods';
import { ACLQueryModeTypes } from 'js/constants/visibilityFilters';
import { getCurrentUser } from 'js/reducers/session';
import { isEmpty } from 'lodash';
import { DraftContextType } from 'new/components/flows/types/genai_rag/context/Draft/DraftContext';
import * as actionTypes from '../../constants/actionTypes';
import { getDenormalizedPipeline } from '../pipelines';
import { deleteLinks } from 'new/state/flowTriggers';
import { pausePipeline, deletePipeline, publish, refreshPipeline } from '../pipelines';

import { API_GENAI_SERVER_URL } from 'js/constants/api';

export const updateRagCache = (flowId: number) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.UPDATE_CACHE_RAG_FLOW_REQUEST,
        actionTypes.UPDATE_CACHE_RAG_FLOW_SUCCESS,
        actionTypes.UPDATE_CACHE_RAG_FLOW_FAILURE,
      ],
      endpoint: `${API_GENAI_SERVER_URL}/flow/update_cache/${flowId}`,
      requestMethod: POST,
      requestInfo: {
        flowId,
      },
    },
  });
};

export const queryRAGFlow =
  (
    flowId: number,
    query: string,
    draft: Pick<DraftContextType, 'draftActions' | 'draftState'> = null,
  ) =>
  (dispatch, getState) => {
    const state = getState();
    const {
      entities: { flowNodes, sources, apiKeys },
    } = state;
    const flow = flowNodes[flowId];
    const sourceId = flow.data_source_id;
    const source = sources[sourceId];
    const apiKeyId = source.api_keys?.[0];
    const apiKey = apiKeys[apiKeyId];

    // find all the LLM sinks
    const llmSinkIds = getFlowSinkIds(flow, flowNodes);

    return dispatch(getDraftFlowDef(flowId, draft)).then((flowDef) =>
      Promise.all(
        llmSinkIds.map((llmSinkId) =>
          dispatch({
            [CALL_PRIVATE_API]: {
              types: [
                actionTypes.QUERY_RAG_FLOW_REQUEST,
                actionTypes.QUERY_RAG_FLOW_SUCCESS,
                actionTypes.QUERY_RAG_FLOW_FAILURE,
              ],
              endpoint: apiKey?.url,
              payload: {
                ...getRAGBasePayload(query, llmSinkId)(dispatch, getState),
                ...(flowDef ? { rag_flow: flowDef } : {}),
              },
              requestMethod: POST,
              requestInfo: {
                flowId,
                llmSinkId,
              },
            },
          }),
        ),
      ),
    );
  };

export const getFlowSinkIds = (flow, flowNodes) => {
  const sinkIds = [];

  if (!flow) return sinkIds;
  if (!flowNodes) return sinkIds;

  if (flow.data_sink_id) {
    return [flow.data_sink_id];
  }

  flow.children
    ?.map((childId) => flowNodes[childId])
    .forEach((child) => {
      sinkIds.push(...getFlowSinkIds(child, flowNodes));
    });

  return sinkIds;
};

const getDraftFlowDef =
  (flowId: number, draft: Pick<DraftContextType, 'draftActions' | 'draftState'>) =>
  (dispatch, getState) => {
    const { draftActions, draftState } = draft;

    if (isEmpty(draftActions) && isEmpty(draftState)) {
      return Promise.resolve(null);
    }

    const {
      entities: { dataSets },
    } = getState();
    const pendingDataSetsByOrigin = draftState.dataSetsPendingLinking?.reduce((acc, dataSetId) => {
      const originNodeId = dataSets[dataSetId].origin_node_id;
      acc[originNodeId] = acc[originNodeId] ? [...acc[originNodeId], dataSetId] : [dataSetId];
      return acc;
    }, {});

    // Handle pending deletions
    const pendingDeletions = {
      dataSets: (draftState.dataSetsPendingDeletion || []).map(
        (dataSetId) => dataSets[dataSetId].origin_node_id,
      ),
      flows: draftState.flowsPendingDeletion || [],
    };

    // fetch the main flow
    return dispatch(getDenormalizedPipeline(flowId)).then(({ result: mainFlow }) =>
      // fetch all flows pending linking (in draftState), and merge them into
      // the main flow
      Promise.all(
        (draftState.flowsPendingLinking || [])
          .concat(
            (draftState.dataSetsPendingLinking || []).map(
              (dataSetId) => dataSets[dataSetId].origin_node_id,
            ),
          )
          .map((retrieverFlowId) => dispatch(getDenormalizedPipeline(retrieverFlowId))),
      ).then((responses) => {
        // First filter out any pending deletions from mainFlow.flows[0]?.linked_flows
        if (mainFlow.flows && mainFlow.flows[0]?.linked_flows) {
          mainFlow.flows[0].linked_flows = mainFlow.flows[0].linked_flows.filter(
            (flowId) =>
              !pendingDeletions.flows.includes(flowId) &&
              !pendingDeletions.dataSets.includes(flowId),
          );
        }

        // Also filter out pending deletions from mainFlow.linked_flows
        if (mainFlow.linked_flows) {
          mainFlow.linked_flows = mainFlow.linked_flows.filter(
            (flow) =>
              !pendingDeletions.flows.includes(flow.id) &&
              !pendingDeletions.dataSets.includes(flow.id),
          );
        }

        responses.forEach(({ result: retrieverFlow }) => {
          // Skip if this retriever flow is in pending deletions
          if (
            pendingDeletions.flows.includes(retrieverFlow.flows[0].id) ||
            pendingDeletions.dataSets.includes(retrieverFlow.flows[0].id)
          ) {
            return;
          }

          // set data_retriever_ids on the flows of dataSetsPendingLinking
          retrieverFlow.flows[0].data_retriever_ids = [
            ...new Set(
              (retrieverFlow.flows[0].data_retriever_ids || []).concat(
                pendingDataSetsByOrigin[retrieverFlow.flows[0].id],
              ),
            ),
          ];

          // add retriever to the linked flows of the main flow
          mainFlow.flows[0]?.linked_flows?.push(retrieverFlow.flows[0].id);
          mainFlow.linked_flows.push(retrieverFlow.flows[0]);

          // merge entities based on id
          const entityKeys = [
            'code_containers',
            'data_sources',
            'data_sets',
            'data_sinks',
            'data_credentials',
            'orgs',
            'users',
          ];
          entityKeys.forEach((entityKey) => {
            const mainEntitiesById =
              mainFlow[entityKey]?.reduce((acc, entity) => {
                acc[entity.id] = entity;
                return acc;
              }, {}) || {};
            retrieverFlow[entityKey]?.forEach((entity) => {
              if (!mainEntitiesById[entity.id]) {
                mainFlow[entityKey]?.push(entity);
              }
            });
          });
        });

        // for each "UPDATE_DATASET_REQUEST" in draftActions, update the
        // dataset
        Object.values(draftActions || {}).forEach(({ args, requestTypes }) => {
          if (requestTypes.includes(actionTypes.UPDATE_DATASET_REQUEST)) {
            const [id, payload] = args[0];
            const dataset = mainFlow.data_sets.find((dataSet) => dataSet.id === id);
            Object.assign(dataset, payload);
          }
        });

        // Filter out pending deleted datasets from data_sets
        if (mainFlow.data_sets && pendingDeletions.dataSets.length > 0) {
          mainFlow.data_sets = mainFlow.data_sets.filter(
            (dataset) => !pendingDeletions.dataSets.includes(dataset.origin_node_id),
          );
        }

        return mainFlow;
      }),
    );
  };

export const getRAGBasePayload = (query: string, llmSinkId: number) => (dispatch, getState) => {
  const state = getState();
  const {
    entities: { sinks },
  } = state;
  const user = getCurrentUser(state);
  const { template_config, data_credentials_id, vendor } = sinks[llmSinkId];
  const model = template_config[`${vendor.name}.rag_llm.model`];
  const provider = template_config[`${vendor.name}.rag_llm.provider`];

  const userId = user?.id ? String(user.id) : '';
  return {
    user_prompt: query,
    user_context: {
      user_id: userId,
      access_role: [ACLQueryModeTypes.MODE_OWNER.searchValue],
      session_id: createHash('sha1').update(userId).digest('hex'),
    },
    llm_config: {
      model,
      provider,
      credential_id: String(data_credentials_id),
    },
  };
};

export const resetRAG = () => ({
  type: actionTypes.RESET_RAG,
});

export const setRAGQuery = (flowId: number, query: string) => ({
  type: actionTypes.SET_RAG_QUERY,
  payload: {
    flowId,
    query,
  },
});

export const setRAGNodeFormData = (flowNodeId: number, formData: any) => ({
  type: actionTypes.SET_RAG_NODE_FORM_DATA,
  payload: {
    flowNodeId,
    formData,
  },
});
